import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  IconButton,
  Typography,
  useTheme,
  CircularProgress,
  alpha,
  SvgIcon,
  SvgIconProps,
  TextField,
  Snackbar, // <-- Import Snackbar
  Alert,    // <-- Import Alert
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { gql, useMutation } from '@apollo/client';

// Tenant details required for the dialog
export interface TenantToDelete {
  id: string;
  name: string;
  allDataSourceCount?: number;
}

// Props for the component
interface DeleteTenantDialogProps {
  open: boolean;
  onClose: () => void;
  onTenantDeleted: (tenantId: string) => void;
  tenantToDelete: TenantToDelete | null;
}

// GraphQL mutation for deleting a tenant
const DELETE_TENANT = gql`
  mutation DeleteTenant($id: ID!) {
    deleteTenant(id: $id) {
      id
      name
    }
  }
`;

// --- Custom icon for the error toast ---
const CustomErrorIcon = () => (
    <Box
      component="span"
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#D92D20',
      }}
    >
      <CloseIcon sx={{ color: 'white', fontSize: '16px' }} />
    </Box>
);

// Custom SVG Icons
const CustomDeleteIcon = (props: SvgIconProps) => (
    <SvgIcon {...props} viewBox="0 0 20 20">
      <path d="M2 6H18V19C18 19.5523 17.5523 20 17 20H3C2.44772 20 2 19.5523 2 19V6ZM4 8V18H16V8H4ZM7 10H9V16H7V10ZM11 10H13V16H11V10ZM5 3V1C5 0.44772 5.44772 0 6 0H14C14.5523 0 15 0.44772 15 1V3H20V5H0V3H5ZM7 2V3H13V2H7Z" fill="#CA2B20"/>
    </SvgIcon>
);
const WarningIcon = (props: SvgIconProps) => (
    <SvgIcon {...props} viewBox="0 0 20 20" >
        <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM9 5H11V11H9V5ZM10 16.5C9.17 16.5 8.5 15.83 8.5 15C8.5 14.17 9.17 13.5 10 13.5C10.83 13.5 11.5 14.17 11.5 15C11.5 15.83 10.83 16.5 10 16.5Z" fill="#A59500 "/>
    </SvgIcon>
);


const DeleteTenantDialog: React.FC<DeleteTenantDialogProps> = ({
  open,
  onClose,
  onTenantDeleted,
  tenantToDelete,
}) => {
  const theme = useTheme();
  const [error, setError] = useState<string | null>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [deleteTenantMutation, { loading: isDeleting }] = useMutation(DELETE_TENANT, {
    onCompleted: (data) => {
      if (tenantToDelete) {
        onTenantDeleted(tenantToDelete.id);
      }
      onClose();
    },
    onError: (err) => {
      // Use the generic error message for the toast
      setToastMessage("We are unable to delete the Tenant at this moment. Please try again after some time.");
      setIsToastOpen(true);
    },
  });

  const handleConfirmDelete = () => {
    setError(null);
    if (!tenantToDelete) {
      setError('Tenant data is missing.');
      return;
    }
    if (confirmationText !== 'Delete') {
      setError('Please type "Delete" to confirm.');
      return;
    }
    deleteTenantMutation({ variables: { id: tenantToDelete.id } });
  };

  React.useEffect(() => {
    if (!open) {
      setError(null);
      setConfirmationText('');
      setIsToastOpen(false);
    }
  }, [open]);

  if (!tenantToDelete) return null;

  const isButtonDisabled = confirmationText !== 'Delete' || isDeleting;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => { if (!isDeleting) onClose(); }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            width: '100%',
            maxWidth: '550px',
          },
        }}
        BackdropProps={{ sx: { backdropFilter: 'blur(5px)' } }}
        sx={{ zIndex: 1302 }}
      >
        <DialogTitle sx={{
          p: theme.spacing(2, 3),
          backgroundImage: `
            linear-gradient(to bottom, transparent 80%, white 100%),
            url('/red-bg-form.svg')
          `,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          borderTopRightRadius: '16px',
          borderTopLeftRadius: '16px',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', pt: '20px' }}>
              <Box component="span" sx={{
                width: 48, height: 48, borderRadius: '8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mr: 1.5, backgroundColor: alpha(theme.palette.error.main, 0.1)
              }}>
                <CustomDeleteIcon sx={{ width: '24px', height: '24px', color: theme.palette.error.main }} />
              </Box>
              <Box>
                <Typography variant="h6" component="div" sx={{
                  fontFamily: 'Inter', fontWeight: 600, fontSize: '16px', pt: '5px', color: 'rgba(3, 23, 52, 1)'
                }}>
                  Delete Tenant
                </Typography>
                <Typography variant="body2" sx={{
                  fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: 'rgba(71, 84, 103, 1)'
                }}>
                  This action is permanent and cannot be undone.
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => { if (!isDeleting) onClose(); }} aria-label="close" sx={{ color: 'text.secondary' }} disabled={isDeleting}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
            <Box sx={{ p: 2, borderRadius: '8px', border: '1px solid #A59500', backgroundColor: '#F6F4E5', display: 'flex', gap: 1.5, alignItems: 'flex-start', mt: 2, mb: 2.5 }}>
                <WarningIcon sx={{ fontSize: '20px', mt: '2px', flexShrink: 0 }} />
                <Box>
                    <Typography sx={{ fontWeight: 500, fontSize: '14px', fontFamily: 'Inter', color: theme.palette.text.primary }}>Please note</Typography>
                    <Typography sx={{ fontSize: '14px', fontFamily: 'Inter', color: theme.palette.text.secondary }}>
                        Upon deleting the tenant it will remove all the data sources and users added under this tenant.
                    </Typography>
                </Box>
            </Box>

            <Typography variant="body1" sx={{ fontWeight: 550, fontFamily: 'Inter', color: theme.palette.text.primary, fontSize: '14px' }}>
              Do you still want to delete this tenant?
            </Typography>

            <Box sx={{ p: 1.5, border: `1px solid ${theme.palette.divider}`, borderRadius: '8px', my: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Inter', fontWeight: 500, color: theme.palette.text.primary }}>
                  {tenantToDelete.name}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'Inter', color: theme.palette.text.secondary, fontSize: '12px' }}>
                  {tenantToDelete.allDataSourceCount ?? 0} Data sources
              </Typography>
            </Box>

            <Typography variant="body2" color="black" sx={{ fontFamily: 'Inter', mb: 1, fontSize: '14px' }}>
              To delete please enter the text "<b>Delete</b>" in the below field.
            </Typography>

            <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Enter text here"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                disabled={isDeleting}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '& fieldset': { borderColor: 'rgba(232, 235, 236, 1)' },
                        '& .MuiInputBase-input': {
                            fontSize: '13px'
                        },
                    },
                }}
            />

            {error && (
              <Typography color="error" sx={{ mt: 2, textAlign: 'center', fontSize: '14px', fontFamily: 'Inter' }}>
                {error}
              </Typography>
            )}
        </DialogContent>

        <DialogActions sx={{ p: theme.spacing(2, 3), pt: 3 }}>
            <Button
              onClick={() => { if (!isDeleting) onClose(); }}
              variant="outlined"
              disabled={isDeleting}
              sx={{
                width: '120px', borderRadius: '8px', color: 'rgba(52, 64, 84, 1)',
                borderColor: 'rgba(208, 213, 221, 1)',
                '&:hover': { borderColor: theme.palette.grey[700], backgroundColor: 'action.hover' },
                py: '8px', textTransform: 'none', fontWeight: 500, fontSize: '14px', fontFamily:'Inter',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              disabled={isButtonDisabled}
              sx={{
                width: '130px', py: '8px', borderRadius:'8px', textTransform: 'none',
                fontWeight: 500, fontSize: '14px', fontFamily:'Inter',
                backgroundColor: theme.palette.error.main,
                '&:hover': { backgroundColor: theme.palette.error.dark },
                '&.Mui-disabled': { backgroundColor: 'rgba(224, 224, 224, 1)', color: 'rgba(173, 173, 173, 1)' }
              }}
            >
              {isDeleting ? <CircularProgress size={24} color="inherit" /> : 'Delete Tenant'}
            </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={isToastOpen}
        autoHideDuration={6000}
        onClose={() => setIsToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          icon={<CustomErrorIcon />} 
          onClose={() => setIsToastOpen(false)}
          sx={{
            width: '100%',
            borderRadius: '50px',
            backgroundColor: 'white',
            boxShadow: theme.shadows[3],
            color: 'text.secondary',
            alignItems: 'center',
            p: theme.spacing(0.5, 2),
            '& .MuiAlert-message': {
              flex: 1,
              py: '12px'
            },
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DeleteTenantDialog;
